import { useState } from "react";
import { ChevronDown, GitMerge, Crown, Check, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ActionButton, CustomerPanel, StatusBadge } from "./customer/CustomerUI";

const MERGE_FIELDS = ["first_name", "last_name", "name", "email", "phone", "company", "website",
  "industry", "address", "city", "state", "postal_code", "country", "tags"];
const PREVIEW = [
  ["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["company", "Company"],
  ["website", "Website"], ["industry", "Industry"], ["city", "City"], ["state", "State"],
];

function computeMerged(members, primaryId) {
  const primary = members.find((m) => m.contact_id === primaryId) || members[0];
  const others = members.filter((m) => m.contact_id !== primary.contact_id);
  const out = {};
  for (const f of MERGE_FIELDS) {
    if (f === "tags") {
      const set = new Set();
      [primary, ...others].forEach((m) => (m.tags || []).forEach((t) => t && set.add(t)));
      out.tags = [...set];
    } else {
      out[f] = primary[f] || (others.find((m) => m[f]) || {})[f] || "";
    }
  }
  return out;
}

export default function MergeCard({ finding, index, connectionId, scanId, onApplied }) {
  const clusters = finding.clusters || [];
  const [open, setOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [primaryMap, setPrimaryMap] = useState(() =>
    Object.fromEntries(clusters.map((c) => [c.id, c.suggested_primary_id])));
  const [selected, setSelected] = useState(() => new Set(clusters.map((c) => c.id)));
  const [mergedMap, setMergedMap] = useState({}); // cluster.id -> merge_request id (applied)

  const setPrimary = (cid, pid) => setPrimaryMap({ ...primaryMap, [cid]: pid });
  const toggle = (cid) => {
    const next = new Set(selected);
    next.has(cid) ? next.delete(cid) : next.add(cid);
    setSelected(next);
  };

  const available = clusters.filter((c) => !mergedMap[c.id]);
  const pending = available.filter((c) => selected.has(c.id));
  const selectAll = () => setSelected(new Set(available.map((c) => c.id)));
  const clearSelection = () => setSelected(new Set());

  const merge = async () => {
    if (pending.length === 0) { toast.error("Select at least one cluster to merge."); return; }
    const merges = pending.map((c) => {
      const pid = primaryMap[c.id] || c.suggested_primary_id;
      const merged = computeMerged(c.members, pid);
      return {
        primary_id: pid, primary_name: merged.name,
        duplicate_ids: c.members.filter((m) => m.contact_id !== pid).map((m) => m.contact_id),
        merged_fields: merged,
      };
    });
    setMerging(true);
    try {
      const { data } = await api.post(`/connections/${connectionId}/apply-merges`, { scan_id: scanId, merges });
      toast.success(`Merged ${data.merged} cluster${data.merged === 1 ? "" : "s"}${data.failed ? `, ${data.failed} failed` : ""}`);
      const applied = { ...mergedMap };
      data.results.filter((r) => r.status === "applied").forEach((r) => { applied[`cluster_${r.primary_id}`] = r.id; });
      setMergedMap(applied);
      onApplied && onApplied();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setMerging(false); }
  };

  const revert = async (clusterId) => {
    const mid = mergedMap[clusterId];
    if (!mid) return;
    try {
      await api.post(`/merge-requests/${mid}/revert`);
      const next = { ...mergedMap }; delete next[clusterId];
      setMergedMap(next);
      toast.success("Merge reverted. Duplicates restored.");
      onApplied && onApplied();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <CustomerPanel data-testid="merge-card"
         className="animate-fade-up border-l-4 border-l-health-critical"
         style={{ animationDelay: `${index * 45}ms` }}>
      <button data-testid="merge-card-toggle" onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="flex min-h-touch w-full items-start justify-between gap-4 rounded-customer p-4 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-health-critical">Critical</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">/ Deduplication</span>
            <StatusBadge tone="success">
              <GitMerge className="h-3 w-3" /> Merge workflow
            </StatusBadge>
          </div>
          <h3 className="mt-1 text-base font-semibold text-slate-900">{finding.title}</h3>
          <p className="mt-0.5 text-sm text-slate-500">{clusters.length} duplicate cluster{clusters.length === 1 ? "" : "s"} · {finding.affected_count} record{finding.affected_count === 1 ? "" : "s"} would be merged away.</p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            <div className="font-mono text-2xl font-bold tracking-tight text-slate-900" data-testid="merge-card-cluster-count">{clusters.length}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">clusters</div>
          </div>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="border-t border-border">
          <div className="bg-red-50 px-4 py-3 text-sm text-slate-700">
            <span className="font-medium">Recommendation: </span>{finding.recommendation}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-white px-4 py-2">
            <span className="text-xs text-slate-500">
              {pending.length} of {available.length} available clusters selected
            </span>
            <div className="flex items-center gap-2">
              <ActionButton type="button" data-testid="merge-card-select-all" onClick={selectAll}
                      disabled={available.length === 0}
                      variant="secondary" size="compact">
                Select all
              </ActionButton>
              <ActionButton type="button" data-testid="merge-card-clear" onClick={clearSelection}
                      disabled={selected.size === 0}
                      variant="quiet" size="compact">
                Clear selection
              </ActionButton>
            </div>
          </div>

          <div className="max-h-[30rem] space-y-4 overflow-auto p-4">
            {clusters.map((c) => {
              const pid = primaryMap[c.id] || c.suggested_primary_id;
              const merged = computeMerged(c.members, pid);
              const done = !!mergedMap[c.id];
              return (
                <div key={c.id} data-testid={`merge-cluster-${c.id}`}
                     className={`border border-border ${done ? "opacity-70" : ""}`}>
                  <div className="flex items-center justify-between gap-3 border-b border-border bg-slate-50 px-3 py-2">
                    <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                      <GitMerge className="h-3.5 w-3.5" /> {c.reason} · {c.members.length} records
                    </span>
                    {done ? (
                      <ActionButton data-testid={`merge-cluster-revert-${c.id}`} onClick={() => revert(c.id)}
                                    variant="secondary" size="compact">
                        <Undo2 className="h-3 w-3" /> Undo merge
                      </ActionButton>
                    ) : (
                      <label className="inline-flex min-h-touch cursor-pointer items-center gap-1.5 text-xs text-slate-600">
                        <input type="checkbox" data-testid={`merge-select-${c.id}`}
                               checked={selected.has(c.id)} onChange={() => toggle(c.id)}
                               className="h-4 w-4 accent-slate-900" /> Include in merge
                      </label>
                    )}
                  </div>

                  <div className="grid gap-px bg-border sm:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
                    {c.members.map((m) => {
                      const isPrimary = m.contact_id === pid;
                      return (
                        <div key={m.contact_id}
                             className={`bg-white p-3 ${isPrimary ? "ring-2 ring-inset ring-brand" : ""}`}>
                          <label className="flex min-h-touch cursor-pointer items-center gap-2">
                            <input type="radio" name={`primary-${c.id}`} disabled={done}
                                   data-testid={`merge-primary-${c.id}-${m.contact_id}`}
                                   checked={isPrimary} onChange={() => setPrimary(c.id, m.contact_id)}
                                   className="h-3.5 w-3.5 accent-slate-900" />
                            <span className="flex items-center gap-1 text-sm font-semibold text-slate-900">
                              {isPrimary && <Crown className="h-3.5 w-3.5 text-brand" />}
                              {m.name || m.contact_id}
                            </span>
                          </label>
                          <div className="mt-2 space-y-0.5 font-mono text-[11px] text-slate-500">
                            <div className="truncate">{m.email || "—"}</div>
                            <div>{m.phone || "—"}</div>
                            <div className="truncate">{m.company || "—"}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t border-border bg-emerald-50/60 px-3 py-2.5">
                    <div className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-700">
                      <Check className="h-3 w-3" /> Merged result (kept record)
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs sm:grid-cols-4"
                         data-testid={`merge-preview-${c.id}`}>
                      {PREVIEW.map(([f, label]) => (
                        <div key={f} className="truncate">
                          <span className="text-slate-400">{label}: </span>
                          <span className="text-slate-800">{merged[f] || "—"}</span>
                        </div>
                      ))}
                      {merged.tags?.length > 0 && (
                        <div className="col-span-2 truncate sm:col-span-4">
                          <span className="text-slate-400">Tags: </span>
                          <span className="text-slate-800">{merged.tags.join(", ")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-white p-3">
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
              <Check className="h-3.5 w-3.5 text-health-good" /> {pending.length} cluster{pending.length === 1 ? "" : "s"} selected · duplicates are deleted, the kept record is updated
            </span>
            <ActionButton data-testid="merge-card-apply" disabled={pending.length === 0 || merging}
                    onClick={merge}
                    size="compact">
              {merging ? "Merging…" : "Merge selected clusters"}
            </ActionButton>
          </div>
        </div>
      )}
    </CustomerPanel>
  );
}
